import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { AuthFetchError, shouldRetryAuthFetch } from "@/lib/authLoadingState";

export function useAuth() {
  // Check URL parameters for role switching and viewAsEmployee
  const urlParams = new URLSearchParams(window.location.search);
  const roleParam = urlParams.get("role");
  const viewAsEmployeeParam = urlParams.get("viewAsEmployee");

  // Resolve viewAsEmployee SYNCHRONOUSLY from the URL (falling back to the
  // persisted value while in the employee role). This used to live in React
  // state updated by an effect, which lagged the URL by one render: the first
  // /api/auth/user fetch after a portal switch went out WITHOUT viewAsEmployee,
  // came back with the account's base role, and the role reconciler flipped
  // portals again — the broker → employee → broker ping-pong that blanked the
  // screen for producers (Brady/Josh, Aug 2026).
  let viewAsEmployee: string | null = null;
  if (viewAsEmployeeParam) {
    viewAsEmployee = viewAsEmployeeParam;
  } else if (roleParam === "employee") {
    try {
      viewAsEmployee = localStorage.getItem("viewAsEmployee");
    } catch {
      viewAsEmployee = null;
    }
  }

  // Persist / clear the stored value as a side effect only.
  useEffect(() => {
    try {
      if (viewAsEmployeeParam) {
        localStorage.setItem("viewAsEmployee", viewAsEmployeeParam);
      } else if (roleParam !== "employee") {
        localStorage.removeItem("viewAsEmployee");
      }
    } catch {
      /* ignore */
    }
  }, [viewAsEmployeeParam, roleParam]);

  // Build query URL with both params if present
  const queryParams = new URLSearchParams();
  if (roleParam) queryParams.set("role", roleParam);
  if (viewAsEmployee) queryParams.set("viewAsEmployee", viewAsEmployee);

  const queryString = queryParams.toString();
  const queryUrl = queryString ? `/api/auth/user?${queryString}` : "/api/auth/user";
  const queryKey = ["/api/auth/user", roleParam || null, viewAsEmployee || null];

  const { data: user, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await fetch(queryUrl, { credentials: "include" });

      if (!response.ok) {
        throw new AuthFetchError(
          `Authentication failed: ${response.status} ${response.statusText}`,
          response.status,
        );
      }

      return await response.json();
    },
    // One retry for a network blip or 5xx before we conclude "signed out";
    // a 401 still drops to Login immediately (BUG-008).
    retry: shouldRetryAuthFetch,
    retryDelay: 500,
    // A portal switch changes the query key; without this the app would see
    // user=undefined (→ "not authenticated") until the new fetch resolves.
    placeholderData: (previous: any) => previous,
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
  };
}